import { useEffect, useState } from "react";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";

import { Dialog, Tooltip } from "@lopie/kernel-ui";

/** `mapping_overrides.CLEAN_FUNCTIONS`' own keys, named rather than
 * reimplemented — the same "render from the kernel" rule the rest of this
 * console follows, held here as a short fixed list because there is no
 * `/schema`-shaped route a form could read six names from instead. */
export const CLEAN_OPTIONS = ["", "str", "email", "phone", "minor_units", "timestamp", "list"];

/**
 * Propose a correction to what a connector sends for one (source, concept)
 * (LO-172, LO-173). Shared by two entry points — a held_reject card
 * (DataQuality.jsx), which has a real failing payload to suggest field names
 * from, and a connector's own mapping screen (Connectors.jsx), which does
 * not. Neither entry point changes what this proposes or how: this proposes
 * and grants nothing, a governor still decides it on the Requests screen,
 * and submitting twice is exactly as permitted afterwards as before.
 *
 * `payloadKeys` is advisory only — an Autocomplete's suggestion list, not a
 * constraint. When it is empty (opened from a connector with no failing
 * example at hand) the fields are still free text; a real payload simply
 * makes the common case faster to fill in correctly.
 */
export function ProposeOverrideDialog({
  open, onClose, source, concept, rejectReason, payloadKeys, post, onProposed,
}) {
  const [reason, setReason] = useState("");
  const [rules, setRules] = useState([
    { payload_path: "", source_path: "", literal: "", clean: "" },
  ]);
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState(null);

  useEffect(() => {
    if (!open) return;
    setReason("");
    setRules([{ payload_path: "", source_path: "", literal: "", clean: "" }]);
    setFailure(null);
  }, [open, source, concept]);

  const keys = payloadKeys || [];

  function updateRule(i, key, value) {
    setRules((prev) => prev.map((r, idx) => (idx === i ? { ...r, [key]: value } : r)));
  }
  function addRule() {
    setRules((prev) => [...prev, { payload_path: "", source_path: "", literal: "", clean: "" }]);
  }
  function removeRule(i) {
    setRules((prev) => prev.filter((_, idx) => idx !== i));
  }

  // Exactly one of source_path/literal per rule, same as the kernel
  // validates — checked here too so a reviewer is never handed a proposal
  // the kernel would 400 on, and so Propose stays disabled until every row
  // actually says something.
  const validRules = rules.filter((r) => {
    const hasSource = Boolean(r.source_path.trim());
    const hasLiteral = Boolean(r.literal.trim());
    return r.payload_path.trim() && hasSource !== hasLiteral;
  });
  const canSubmit = Boolean(reason.trim()) && validRules.length === rules.length
    && validRules.length > 0;

  async function submit() {
    setBusy(true);
    setFailure(null);
    try {
      const result = await post("/map-override-requests", {
        source, concept, reason: reason.trim(),
        rules: validRules.map((r) => ({
          payload_path: r.payload_path.trim(),
          source_path: r.source_path.trim(),
          literal: r.literal.trim(),
          clean: r.clean,
        })),
      });
      onProposed(result);
      onClose();
    } catch (e) {
      setFailure(e);
    } finally {
      setBusy(false);
    }
  }

  return (
    <Dialog open={open} onClose={busy ? undefined : onClose} maxWidth="sm"
      fullWidth aria-labelledby="override-title">
      <DialogTitle id="override-title">Propose a mapping fix</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          <Alert severity="info" variant="outlined" icon={false}>
            <Typography variant="body2">
              This proposes a correction; it grants nothing by itself. A
              governor still has to approve it on Requests before{" "}
              <code>{source}</code>&apos;s <code>{concept}</code> reads any
              differently than it does today. Approving does not rewrite an
              existing record — a Replay afterward is what does.
            </Typography>
          </Alert>

          <Typography variant="body2" color="text.secondary">
            <code>{source}</code> / <code>{concept}</code>
            {rejectReason ? (
              <>
                , rejected with <code>{rejectReason}</code>
              </>
            ) : null}
            .
          </Typography>

          <TextField
            label="Why"
            multiline
            minRows={2}
            fullWidth
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            disabled={busy}
            placeholder="Contacts synced with no name and no email are rejected outright; fill a placeholder so the person record still lands."
          />

          <Box>
            <Typography variant="overline" color="text.secondary" sx={{ display: "block", mb: 0.5 }}>
              Fill a blank payload field
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1 }}>
              Only fires when the field is already blank. Fill it from
              another field on the same record, or from a fixed value — not
              both.
              {!keys.length ? (
                " No real payload to suggest field names from here — type "
                + "them directly, the same way a reject card's evidence "
                + "names them."
              ) : null}
            </Typography>
            <Stack spacing={1.5}>
              {rules.map((r, i) => (
                <Stack key={i} spacing={1} sx={{ p: 1, border: "1px solid", borderColor: "divider", borderRadius: 1 }}>
                  <Autocomplete
                    freeSolo
                    size="small"
                    options={keys}
                    inputValue={r.payload_path}
                    onInputChange={(e, value) => updateRule(i, "payload_path", value)}
                    disabled={busy}
                    renderInput={(params) => (
                      <TextField {...params} label="Payload field to fill" />
                    )}
                  />
                  <Stack direction="row" spacing={1} sx={{ flexWrap: "wrap" }}>
                    <Autocomplete
                      freeSolo
                      size="small"
                      options={keys}
                      inputValue={r.source_path}
                      onInputChange={(e, value) => updateRule(i, "source_path", value)}
                      disabled={busy || Boolean(r.literal.trim())}
                      sx={{ minWidth: 180, flex: 1 }}
                      renderInput={(params) => (
                        <TextField {...params} label="From another field" />
                      )}
                    />
                    <TextField
                      size="small"
                      label="Or a fixed value"
                      value={r.literal}
                      onChange={(e) => updateRule(i, "literal", e.target.value)}
                      disabled={busy || Boolean(r.source_path.trim())}
                      sx={{ minWidth: 180, flex: 1 }}
                    />
                    <TextField
                      size="small"
                      select
                      label="Cleaned with"
                      value={r.clean}
                      onChange={(e) => updateRule(i, "clean", e.target.value)}
                      disabled={busy}
                      sx={{ minWidth: 140 }}
                    >
                      {CLEAN_OPTIONS.map((c) => (
                        <MenuItem key={c || "none"} value={c}>{c || "none"}</MenuItem>
                      ))}
                    </TextField>
                  </Stack>
                  <Stack direction="row" sx={{ justifyContent: "flex-end" }}>
                    <Button size="small" color="inherit" disabled={busy || rules.length === 1}
                      onClick={() => removeRule(i)}>
                      Remove
                    </Button>
                  </Stack>
                </Stack>
              ))}
              <Button size="small" variant="outlined" onClick={addRule} disabled={busy}>
                Add another field
              </Button>
            </Stack>
          </Box>

          {failure ? (
            <Alert severity="error" variant="outlined">
              {failure.body?.detail || failure.message}
            </Alert>
          ) : null}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>Cancel</Button>
        <Button variant="contained" disabled={busy || !canSubmit} onClick={submit}>
          {busy ? "Proposing…" : "Propose fix"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

/**
 * Re-run every currently-held reject for this source that failed with this
 * rule, against the mapping and any approved override, right now (LO-151,
 * LO-172). Scoped to one rule rather than every reject the source holds — a
 * person reading one card and clicking Replay should not be told it quietly
 * rewrote an unrelated one too.
 */
export function ReplayControl({ source, rule, mayGovern, onReplay }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [outcome, setOutcome] = useState(null);

  async function confirm() {
    setBusy(true);
    try {
      const result = await onReplay(source, rule);
      setOutcome({
        ok: true,
        detail: `Created ${result.created}, updated ${result.updated} — `
          + `${result.still_rejected} still rejected.`,
      });
    } catch (e) {
      setOutcome({ ok: false, detail: e?.body?.detail || e?.message || String(e) });
    } finally {
      setBusy(false);
      setOpen(false);
    }
  }

  return (
    <>
      <Tooltip
        title={
          mayGovern === false
            ? "You are not on this kernel's governor list — a replay writes " +
              "real records, the same consequential-decision gate rollback " +
              "and approving an override both need."
            : `Re-runs every record ${source} currently holds with this ` +
              "rule, against the mapping and any approved override, right now."
        }
      >
        <span>
          <Button size="small" variant="outlined" disabled={mayGovern !== true || busy}
            onClick={() => setOpen(true)}>
            Replay
          </Button>
        </span>
      </Tooltip>

      <Dialog open={open} onClose={busy ? undefined : () => setOpen(false)}
        maxWidth="sm" fullWidth aria-labelledby="replay-title">
        <DialogTitle id="replay-title">Replay held rejects</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={1.5}>
            <Alert severity="info" variant="outlined" icon={false}>
              <Typography variant="body2">
                Re-runs every currently-held reject for <code>{source}</code>{" "}
                that failed with <code>{rule}</code> through the mapping, with
                whatever correction is approved right now. One pass, not a
                guarantee the queue is empty afterward — a payload the
                correction does not cover stays held.
              </Typography>
            </Alert>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)} disabled={busy}>Cancel</Button>
          <Button variant="contained" disabled={busy} onClick={confirm}>
            {busy ? "Replaying…" : "Replay"}
          </Button>
        </DialogActions>
      </Dialog>

      {outcome ? (
        <Alert
          severity={outcome.ok ? "success" : "error"}
          variant="outlined"
          onClose={() => setOutcome(null)}
          sx={{ mt: 1 }}
        >
          {outcome.detail}
        </Alert>
      ) : null}
    </>
  );
}

/**
 * A live override's rules, read for anyone — the same table shape
 * `MapOverrideBody` (Requests.jsx) renders for a pending proposal, reused
 * here for an already-approved one so the two never describe a rule
 * differently.
 */
export function OverrideRulesTable({ rules }) {
  return (
    <Box sx={{ overflowX: "auto" }}>
      <Table size="small">
        <TableBody>
          <TableRow>
            <TableCell sx={{ border: 0, fontWeight: 600 }}>Payload field</TableCell>
            <TableCell sx={{ border: 0, width: 30 }} />
            <TableCell sx={{ border: 0, fontWeight: 600 }}>Filled from, when blank</TableCell>
            <TableCell sx={{ border: 0, fontWeight: 600 }}>Cleaned with</TableCell>
          </TableRow>
          {(rules || []).map((r, i) => (
            <TableRow key={i}>
              <TableCell sx={{ border: 0, fontFamily: "monospace" }}>
                {r.payload_path}
              </TableCell>
              <TableCell sx={{ border: 0, color: "text.secondary" }}>&rarr;</TableCell>
              <TableCell sx={{ border: 0, fontFamily: "monospace" }}>
                {r.source_path ? r.source_path : `literal "${r.literal}"`}
              </TableCell>
              <TableCell sx={{ border: 0 }}>{r.clean || "—"}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Box>
  );
}
